package com.terapia.terasenior.ui.therapy

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.terapia.terasenior.domain.model.patient.Patient
import com.terapia.terasenior.domain.model.therapy.ExerciseConfig
import com.terapia.terasenior.domain.model.therapy.SessionMode
import com.terapia.terasenior.treatment.repository.TherapeuticExerciseCatalog
import com.terapia.terasenior.treatment.repository.LiteracyExerciseCatalog
import com.terapia.terasenior.treatment.repository.PerceptionExerciseCatalog
import com.terapia.terasenior.treatment.repository.LanguageExerciseCatalog
import com.terapia.terasenior.treatment.repository.AttentionExerciseCatalog
import com.terapia.terasenior.treatment.repository.MemoryExerciseCatalog
import com.terapia.terasenior.treatment.repository.OrientationExerciseCatalog
import com.terapia.terasenior.ui.therapy.ExerciseTranslationUtils
import com.terapia.terasenior.ui.components.LocalTeraseniorLayout
import com.terapia.terasenior.ui.components.ListScrollPositionBar

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreateSessionWizard(
    viewModel: CreateSessionViewModel,
    therapistId: String,
    onSessionCreated: (String) -> Unit,
    onCancel: () -> Unit
) {
    val uiState by viewModel.uiState.collectAsState()

    LaunchedEffect(uiState.createdSessionId) {
        uiState.createdSessionId?.let { onSessionCreated(it) }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Nueva Sesión") },
                navigationIcon = {
                    IconButton(onClick = {
                        if (uiState.currentStep == WizardStep.MODE_SELECTION) onCancel()
                        else viewModel.goBack()
                    }) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Volver")
                    }
                }
            )
        }
    ) { padding ->
        Box(modifier = Modifier.fillMaxSize().padding(padding)) {
            when (uiState.currentStep) {
                WizardStep.MODE_SELECTION -> ModeSelectionStep(onModeSelected = viewModel::onModeSelected)
                WizardStep.PATIENT_SELECTION -> PatientSelectionStep(
                    patients = uiState.patients,
                    isLoading = uiState.isLoading,
                    onPatientSelected = viewModel::onPatientSelected
                )
                WizardStep.CATEGORY_SELECTION -> CategorySelectionStep(onCategorySelected = viewModel::onCategorySelected)
                WizardStep.EXERCISE_SELECTION -> ExerciseSelectionStep(
                    category = uiState.selectedCategory ?: "",
                    selectedExercises = uiState.selectedExercises,
                    onToggle = viewModel::toggleExercise,
                    onNext = viewModel::goNextFromExercises
                )
                WizardStep.LEVEL_SELECTION -> LevelSelectionStep(onLevelSelected = viewModel::onLevelSelected)
                WizardStep.QUICK_LEVEL_SELECTION -> LevelSelectionStep(onLevelSelected = viewModel::onQuickLevelSelected)
                WizardStep.SUMMARY -> SessionSummaryStep(
                    uiState = uiState,
                    onCreateSession = { viewModel.createSession(therapistId) }
                )
            }
        }
    }
}

@Composable
private fun ModeSelectionStep(onModeSelected: (SessionMode) -> Unit) {
    Column(
        modifier = Modifier.fillMaxSize().padding(24.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text("Modalidad de trabajo", style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Bold)
        SelectionCard(
            title = "Con Paciente",
            description = "Registro clínico y seguimiento.",
            icon = Icons.Default.Person,
            onClick = { onModeSelected(SessionMode.WITH_PATIENT) }
        )
        SelectionCard(
            title = "Sin Paciente",
            description = "Uso libre o demostración.",
            icon = Icons.Default.PersonOff,
            onClick = { onModeSelected(SessionMode.WITHOUT_PATIENT) }
        )
    }
}

@Composable
private fun PatientSelectionStep(patients: List<Patient>, isLoading: Boolean, onPatientSelected: (Patient) -> Unit) {
    Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
        Text("Selecciona el paciente", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
        Spacer(modifier = Modifier.height(16.dp))
        if (isLoading) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { CircularProgressIndicator() }
        } else {
            LazyColumn(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                items(patients) { patient ->
                    Card(onClick = { onPatientSelected(patient) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(16.dp)) {
                        Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                            Box(modifier = Modifier.size(40.dp).clip(CircleShape).background(MaterialTheme.colorScheme.primaryContainer), contentAlignment = Alignment.Center) {
                                Text(patient.firstName.take(1))
                            }
                            Spacer(modifier = Modifier.width(16.dp))
                            Text(patient.fullName, fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CategorySelectionStep(onCategorySelected: (String) -> Unit) {
    val layout = LocalTeraseniorLayout.current
    Column(
        modifier = Modifier.fillMaxSize().padding(horizontal = if (layout.isCompactWidth) 16.dp else 24.dp, vertical = 16.dp)
    ) {
        Text("Área Cognitiva", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
        Spacer(modifier = Modifier.height(16.dp))

        val categories = listOf(
            Quadruple("Orientación", "Tiempo, espacio y persona.", Icons.Default.Event, OrientationExerciseCatalog.items.size),
            Quadruple("Atención", "Focalización y mantenimiento visual.", Icons.Default.Visibility, AttentionExerciseCatalog.items.size),
            Quadruple("Memoria", "Codificación y recuperación.", Icons.Default.Psychology, MemoryExerciseCatalog.items.size),
            Quadruple("Lenguaje", "Fluidez y comprensión.", Icons.Default.RecordVoiceOver, LanguageExerciseCatalog.items.size),
            Quadruple("Funciones Ejecutivas", "Planificación, secuencias y cálculo.", Icons.Default.Settings, TherapeuticExerciseCatalog.items.size),
            Quadruple("Percepción", "Gnosias y capacidades visoespaciales.", Icons.Default.Extension, PerceptionExerciseCatalog.items.size),
            Quadruple("Lectoescritura", "Grafomotricidad y trazos.", Icons.Default.Edit, LiteracyExerciseCatalog.items.size)
        )

        LazyVerticalGrid(
            columns = GridCells.Fixed(if (layout.isCompactWidth) 1 else 2),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.fillMaxSize()
        ) {
            items(categories) { (title, desc, icon, activityCount) ->
                SelectionCard(
                    title = title,
                    description = desc,
                    icon = icon,
                    activityCount = activityCount,
                    onClick = { onCategorySelected(title) }
                )
            }
        }
    }
}

@Composable
private fun ExerciseSelectionStep(
    category: String,
    selectedExercises: List<ExerciseConfig>,
    onToggle: (type: String, name: String, category: String, desc: String) -> Unit,
    onNext: () -> Unit
) {
    val layout = LocalTeraseniorLayout.current
    val exercises = remember(category) {
        when (category) {
            "Orientación" -> OrientationExerciseCatalog.forCategory(category)
                .map { Triple(it.id, it.name, it.description) }
                .sortedBy { it.second }
            "Atención" -> AttentionExerciseCatalog.forCategory(category)
                .map { Triple(it.id, it.name, it.description) }
                .sortedBy { it.second }
            "Memoria" -> MemoryExerciseCatalog.forCategory(category)
                .map { Triple(it.id, it.name, it.description) }
                .sortedBy { it.second }
            "Lenguaje" -> LanguageExerciseCatalog.forCategory(category)
                .map { Triple(it.id, it.name, it.description) }
                .sortedBy { it.second }
            "Funciones Ejecutivas" -> TherapeuticExerciseCatalog
                .forCategory(category)
                .map { Triple(it.id, it.name, it.description) }
                .sortedBy { it.second }
            "Percepción" -> PerceptionExerciseCatalog.forCategory(category)
                .map { Triple(it.id, it.name, it.description) }
                .sortedBy { it.second }
            "Lectoescritura" -> LiteracyExerciseCatalog.forCategory(category)
                .map { Triple(it.id, it.name, it.description) }
                .sortedBy { it.second }
            else -> emptyList()
        }
    }

    val parts = remember(exercises) {
        exercises.groupBy { exercisePartKey(it.first) }
            .map { (key, entries) ->
                ExercisePart(key, exercisePartTitle(category, key), entries.sortedBy { exerciseOrdinal(it.first) })
            }
            .sortedBy { exercisePartOrdinal(it.title) }
    }
    var selectedPartKey by remember(category) { mutableStateOf<String?>(null) }
    var searchQuery by remember(selectedPartKey) { mutableStateOf("") }
    val selectedPart = parts.firstOrNull { it.key == selectedPartKey }
    val filteredExercises = remember(selectedPart, searchQuery) {
        selectedPart?.exercises.orEmpty().filter {
            searchQuery.isBlank() || it.second.contains(searchQuery, ignoreCase = true)
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = if (layout.isCompactWidth) 16.dp else 24.dp, vertical = if (layout.isCompactHeight) 12.dp else 16.dp),
        contentAlignment = Alignment.TopCenter
    ) {
    Column(
        modifier = Modifier
            .widthIn(max = 1120.dp)
            .fillMaxWidth()
            .fillMaxSize()
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = selectedPart?.let { "${it.title}" } ?: "Partes de $category",
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.weight(1f)
            )
            Text("${selectedExercises.size} seleccionados", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary)
        }
        
        Spacer(modifier = Modifier.height(12.dp))

        if (selectedPart != null) {
            TextButton(onClick = { selectedPartKey = null }) {
                Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = null)
                Spacer(Modifier.width(8.dp))
                Text("Volver a las partes")
            }
            OutlinedTextField(
                value = searchQuery,
                onValueChange = { searchQuery = it },
                placeholder = { Text("Buscar en esta parte...", fontSize = 14.sp) },
                leadingIcon = { Icon(Icons.Default.Search, null, modifier = Modifier.size(20.dp)) },
                modifier = Modifier.fillMaxWidth().height(52.dp),
                shape = RoundedCornerShape(12.dp),
                singleLine = true
            )
            Spacer(modifier = Modifier.height(12.dp))
        } else {
            Text(
                "Elige una parte para ver sus ejercicios concretos.",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Spacer(modifier = Modifier.height(12.dp))
        }

        val listState = rememberLazyListState()
        Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
            LazyColumn(
                state = listState,
                modifier = Modifier.fillMaxSize(),
                verticalArrangement = Arrangement.spacedBy(6.dp),
                contentPadding = PaddingValues(bottom = 8.dp)
            ) {
                if (selectedPart == null) {
                    items(parts) { part ->
                        PartItem(
                            title = part.title,
                            description = "${part.exercises.size} ejercicios disponibles",
                            onClick = { selectedPartKey = part.key }
                        )
                    }
                } else {
                    items(filteredExercises.withIndex().toList()) { indexed ->
                        val (type, name, desc) = indexed.value
                        ExerciseItem(
                            title = "Ejercicio ${indexed.index + 1}: $name",
                            isSelected = selectedExercises.any { it.type == type },
                            onToggle = { onToggle(type, name, category, desc) }
                        )
                    }
                }
            }
        }

        ListScrollPositionBar(
            state = listState,
            itemCount = if (selectedPart == null) parts.size else filteredExercises.size,
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 4.dp)
        )
        
        Spacer(modifier = Modifier.height(if (layout.isCompactHeight) 8.dp else 16.dp))

        Button(
            onClick = onNext,
            modifier = Modifier.fillMaxWidth().height(if (layout.isCompactHeight) 52.dp else 56.dp),
            shape = RoundedCornerShape(16.dp),
            enabled = selectedExercises.isNotEmpty()
        ) {
            Text("Siguiente")
        }
    }
    }
}

private data class ExercisePart(
    val key: String,
    val title: String,
    val exercises: List<Triple<String, String, String>>
)

private fun exercisePartOrdinal(title: String): Int =
    Regex("""Parte\s+(\d+)""").find(title)?.groupValues?.getOrNull(1)?.toIntOrNull() ?: Int.MAX_VALUE

private fun exerciseOrdinal(type: String): Int = type.substringAfterLast("_").toIntOrNull() ?: Int.MAX_VALUE

private fun exercisePartKey(type: String): String {
    val parts = type.split("_")
    return if (parts.firstOrNull() == "guided" && parts.size >= 3) parts[2] else type
}

private fun exercisePartTitle(category: String, key: String): String {
    when (category) {
        "Lectoescritura" -> return when (key) {
            "initial" -> "Parte 1 · Letra inicial"
            "final" -> "Parte 2 · Letra final"
            "syllable" -> "Parte 3 · Completar sílabas"
            "word" -> "Parte 4 · Palabra e imagen"
            "useful" -> "Parte 5 · Palabras útiles"
            "sentence" -> "Parte 6 · Frases cotidianas"
            "reading" -> "Parte 7 · Comprensión lectora"
            "order" -> "Parte 8 · Orden de palabras"
            "rhyme" -> "Parte 9 · Sonidos parecidos"
            "writing" -> "Parte 10 · Escritura funcional"
            else -> "Parte · ${key.replaceFirstChar { it.uppercase() }}"
        }
        "Memoria" -> return when (key) {
            "visual" -> "Parte 1 · Recuerdo visual"
            "association" -> "Parte 2 · Asociaciones"
            "category" -> "Parte 3 · Recuerdo por categoría"
            "location" -> "Parte 4 · Dónde estaba"
            "use" -> "Parte 5 · Para qué sirve"
            "sequence" -> "Parte 6 · Secuencias"
            "daily" -> "Parte 7 · Memoria cotidiana"
            "pairs" -> "Parte 8 · Parejas"
            "order" -> "Parte 9 · Orden temporal"
            "words" -> "Parte 10 · Palabras recordadas"
            else -> "Parte · ${key.replaceFirstChar { it.uppercase() }}"
        }
        "Percepción" -> return when (key) {
            "identify" -> "Parte 1 · Reconocer objetos"
            "category" -> "Parte 2 · Clasificar objetos"
            "function" -> "Parte 3 · Uso de objetos"
            "color" -> "Parte 4 · Color predominante"
            "shape" -> "Parte 5 · Forma general"
            "detail" -> "Parte 6 · Detalle visual"
            "position" -> "Parte 7 · Orientación espacial"
            "body" -> "Parte 8 · Percepción corporal"
            "difference" -> "Parte 9 · Discriminación visual"
            "context" -> "Parte 10 · Contexto cotidiano"
            else -> "Parte · ${key.replaceFirstChar { it.uppercase() }}"
        }
    }
    return exercisePartTitle(key)
}

private fun exercisePartTitle(key: String): String = when (key) {
    "week" -> "Parte 1 · Días de la semana"
    "month" -> "Parte 2 · Meses del año"
    "season" -> "Parte 3 · Estaciones"
    "daytime" -> "Parte 4 · Momentos del día"
    "home" -> "Parte 5 · Orientación en casa"
    "community" -> "Parte 6 · Lugares del entorno"
    "time" -> "Parte 7 · Uso del tiempo"
    "weather" -> "Parte 8 · Tiempo y preparación"
    "routine" -> "Parte 9 · Rutinas diarias"
    "situation" -> "Parte 10 · Situaciones cotidianas"
    "target" -> "Parte 1 · Reconocer el objetivo"
    "same" -> "Parte 2 · Buscar iguales"
    "different" -> "Parte 3 · Encontrar diferencias"
    "count" -> "Parte 4 · Conteo atento"
    "sequence" -> "Parte 5 · Secuencias"
    "category" -> "Parte 6 · Categorías"
    "color" -> "Parte 7 · Colores"
    "position" -> "Parte 8 · Posiciones"
    "rule" -> "Parte 9 · Cambio de regla"
    "daily" -> "Parte 10 · Situaciones diarias"
    "visual" -> "Parte 1 · Recuerdo visual"
    "association" -> "Parte 2 · Asociaciones"
    "location" -> "Parte 4 · Dónde estaba"
    "use" -> "Parte 5 · Para qué sirve"
    "pairs" -> "Parte 8 · Parejas"
    "order" -> "Parte 9 · Orden temporal"
    "words" -> "Parte 10 · Palabras recordadas"
    "naming" -> "Parte 1 · Nombrar objetos"
    "function" -> "Parte 3 · Palabras y uso"
    "initial" -> "Parte 4 · Letra inicial"
    "syllable" -> "Parte 5 · Completar sílabas"
    "opposites" -> "Parte 6 · Palabras opuestas"
    "sentences" -> "Parte 7 · Completar frases"
    "comprehension" -> "Parte 8 · Comprensión"
    "expressions" -> "Parte 9 · Expresiones cotidianas"
    "communication" -> "Parte 10 · Comunicación práctica"
    "countdown" -> "Parte 1 · Cuenta atrás"
    "decisions" -> "Parte 2 · Decisiones cotidianas"
    "emotions" -> "Parte 3 · Reconocer emociones"
    "rhythms" -> "Parte 4 · Ritmos y secuencias"
    "calculation" -> "Parte 5 · Cálculo con atención"
    "reverse" -> "Parte 6 · Series al revés"
    "flexibility" -> "Parte 7 · Frases alternativas"
    "stories" -> "Parte 8 · Historias encadenadas"
    "logic" -> "Parte 9 · Problemas sencillos"
    "planning" -> "Parte 10 · Planifica la tarea"
    "identify" -> "Parte 1 · Reconocer objetos"
    "detail" -> "Parte 6 · Detalles visuales"
    "body" -> "Parte 8 · Percepción corporal"
    "difference" -> "Parte 9 · Discriminación visual"
    "context" -> "Parte 10 · Contexto cotidiano"
    "final" -> "Parte 2 · Letra final"
    "word" -> "Parte 4 · Palabra e imagen"
    "useful" -> "Parte 5 · Palabras útiles"
    "reading" -> "Parte 7 · Comprensión lectora"
    "rhyme" -> "Parte 9 · Sonidos parecidos"
    "writing" -> "Parte 10 · Escritura funcional"
    else -> "Parte · ${key.replaceFirstChar { it.uppercase() }}"
}

@Composable
private fun PartItem(title: String, description: String, onClick: () -> Unit) {
    Surface(
        onClick = onClick,
        shape = RoundedCornerShape(12.dp),
        color = MaterialTheme.colorScheme.surface,
        border = CardDefaults.outlinedCardBorder(),
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
            Column(modifier = Modifier.weight(1f)) {
                Text(title, fontWeight = FontWeight.Bold)
                Text(description, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
            Icon(Icons.Default.ChevronRight, contentDescription = "Ver ejercicios")
        }
    }
}

@Composable
private fun ExerciseItem(title: String, isSelected: Boolean, onToggle: () -> Unit, enabled: Boolean = true) {
    Surface(
        onClick = if(enabled) onToggle else ({}),
        shape = RoundedCornerShape(8.dp),
        color = if(isSelected) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surface,
        border = if(!isSelected) CardDefaults.outlinedCardBorder() else null,
        enabled = enabled,
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp), verticalAlignment = Alignment.CenterVertically) {
            Checkbox(checked = isSelected, onCheckedChange = { onToggle() }, enabled = enabled)
            Spacer(modifier = Modifier.width(12.dp))
            Text(title, style = MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.Medium), color = if(enabled) Color.Unspecified else Color.Gray)
        }
    }
}

@Composable
private fun LevelSelectionStep(onLevelSelected: (Int) -> Unit) {
    Column(modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(24.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text("Nivel de Dificultad (GDS)", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
        (1..5).forEach { level ->
            Card(onClick = { onLevelSelected(level) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(16.dp)) {
                Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                    Text(ExerciseTranslationUtils.getGdsLabel(level), fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                    Icon(Icons.Default.ChevronRight, contentDescription = null)
                }
            }
        }
    }
}

@Composable
private fun SessionSummaryStep(uiState: CreateSessionUiState, onCreateSession: () -> Unit) {
    Column(modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(24.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
        Text("Confirmar Sesión", style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Bold)
        Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))) {
            Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                SummaryRow("Paciente", uiState.selectedPatient?.fullName ?: "Sesión Libre")
                SummaryRow("Ejercicios", "${uiState.selectedExercises.size} seleccionados")
            }
        }
        Spacer(modifier = Modifier.weight(1f))
        Button(onClick = onCreateSession, modifier = Modifier.fillMaxWidth().height(56.dp), shape = RoundedCornerShape(16.dp), enabled = !uiState.isLoading) {
            if (uiState.isLoading) CircularProgressIndicator(color = Color.White) else Text("Iniciar Sesión", fontSize = 18.sp)
        }
    }
}

@Composable
private fun SummaryRow(label: String, value: String) {
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, style = MaterialTheme.typography.bodyMedium, color = Color.Gray)
        Text(value, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold)
    }
}

private fun getMemoryPool(): List<Triple<String, String, String>> {
    return listOf(
        Triple("memory_pairs", "Parejas de Memoria", "Encuentra las parejas de cartas iguales."),
        Triple("memory_cultural_inventor", "Inventor de bombilla", "Memoria cultural histórica."),
        Triple("memory_cultural_discovery", "Descubrimiento América", "Memoria cultural histórica."),
        Triple("memory_cultural_moon", "Llegada a la Luna", "Memoria cultural histórica."),
        Triple("memory_cultural_painting", "Pintor Las Meninas", "Memoria cultural artística."),
        Triple("memory_cultural_writer", "Autor El Quijote", "Memoria cultural literaria."),
        Triple("memory_recent_today_weather", "Tiempo de hoy", "Memoria episódica reciente."),
        Triple("memory_recent_last_meal", "Cena de anoche", "Memoria episódica reciente."),
        Triple("memory_prospective_meds", "Hora medicación", "Memoria prospectiva."),
        Triple("memory_working_numbers_rev", "Números al revés", "Memoria de trabajo."),
        Triple("memory_semantic_capital_italy", "Capital de Italia", "Memoria semántica geográfica."),
        Triple("memory_semantic_capital_germany", "Capital de Alemania", "Memoria semántica geográfica."),
        Triple("memory_semantic_capital_portugal", "Capital de Portugal", "Memoria semántica geográfica."),
        Triple("memory_semantic_ocean_depth", "Animal mayor océano", "Memoria semántica biológica."),
        Triple("memory_semantic_planet_red", "Planeta rojo", "Memoria semántica astronómica."),
        Triple("memory_semantic_island_canary", "Isla de Canarias", "Memoria semántica geográfica."),
        Triple("memory_semantic_island_balearic", "Isla de Baleares", "Memoria semántica geográfica."),
        Triple("memory_semantic_river_egypt", "Río de Egipto", "Memoria semántica geográfica."),
        Triple("memory_semantic_mountain_everest", "Montaña más alta", "Memoria semántica geográfica."),
        Triple("memory_semantic_continent_kangaroo", "Continente canguros", "Memoria semántica biológica."),
        Triple("memory_semantic_continent_pyramids", "Continente pirámides", "Memoria semántica geográfica."),
        Triple("memory_utility_hammer", "Uso del martillo", "Memoria instrumental."),
        Triple("memory_utility_scissors", "Uso de tijeras", "Memoria instrumental."),
        Triple("memory_utility_broom", "Uso de la escoba", "Memoria instrumental."),
        Triple("memory_utility_keys", "Uso de las llaves", "Memoria instrumental."),
        Triple("memory_utility_glasses", "Uso de las gafas", "Memoria instrumental."),
        Triple("memory_needs_coffee", "Necesidad para café", "Memoria de procesos."),
        Triple("memory_needs_letter", "Necesidad para carta", "Memoria de procesos."),
        Triple("memory_needs_wash_hair", "Necesidad para pelo", "Memoria de procesos."),
        Triple("memory_needs_teeth", "Necesidad para dientes", "Memoria de procesos."),
        Triple("memory_needs_rain", "Necesidad para lluvia", "Memoria de procesos."),
        Triple("memory_semantic_fruit_yellow", "Fruta amarilla", "Memoria semántica visual."),
        Triple("memory_semantic_fruit_red", "Fruta roja", "Memoria semántica visual."),
        Triple("memory_semantic_animal_bark", "Animal que ladra", "Memoria semántica onomatopéyica."),
        Triple("memory_semantic_animal_meow", "Animal que maúlla", "Memoria semántica onomatopéyica."),
        Triple("memory_semantic_animal_moo", "Animal que muge", "Memoria semántica onomatopéyica."),
        Triple("memory_semantic_color_grass", "Color de hierba", "Memoria semántica visual."),
        Triple("memory_semantic_color_sky", "Color del cielo", "Memoria semántica visual."),
        Triple("memory_semantic_color_coal", "Color del carbón", "Memoria semántica visual."),
        Triple("memory_semantic_color_milk", "Color de leche", "Memoria semántica visual."),
        Triple("memory_semantic_color_blood", "Color de sangre", "Memoria semántica visual."),
        Triple("memory_semantic_season_cold", "Estación más fría", "Memoria semántica temporal."),
        Triple("memory_semantic_season_hot", "Estación más calurosa", "Memoria semántica temporal."),
        Triple("memory_semantic_day_first", "Primer día semana", "Memoria semántica temporal."),
        Triple("memory_semantic_day_last", "Último día semana", "Memoria semántica temporal."),
        Triple("memory_semantic_month_first", "Primer mes año", "Memoria semántica temporal."),
        Triple("memory_semantic_clothing_feet", "Prenda para pies", "Memoria semántica instrumental."),
        Triple("memory_semantic_clothing_hands", "Prenda para manos", "Memoria semántica instrumental."),
        Triple("memory_semantic_clothing_head", "Prenda para cabeza", "Memoria semántica instrumental."),
        Triple("memory_semantic_tool_nails", "Herramienta clavos", "Memoria semántica instrumental."),
        Triple("memory_semantic_tool_screws", "Herramienta tornillos", "Memoria semántica instrumental."),
        Triple("memory_semantic_kitchen_fry", "Objeto para freír", "Memoria semántica cocina."),
        Triple("memory_semantic_kitchen_soup", "Objeto para sopa", "Memoria semántica cocina."),
        Triple("memory_semantic_kitchen_bake", "Lugar para asar", "Memoria semántica cocina."),
        Triple("memory_semantic_home_sleep", "Mueble para dormir", "Memoria semántica hogar."),
        Triple("memory_semantic_home_sit", "Mueble para sentarse", "Memoria semántica hogar."),
        Triple("memory_semantic_home_clothes", "Lugar para ropa", "Memoria semántica hogar."),
        Triple("memory_semantic_home_food", "Lugar para comida", "Memoria semántica hogar."),
        Triple("memory_semantic_body_see", "Parte para ver", "Memoria semántica cuerpo."),
        Triple("memory_semantic_body_hear", "Parte para oír", "Memoria semántica cuerpo."),
        Triple("memory_semantic_body_smell", "Parte para oler", "Memoria semántica cuerpo."),
        Triple("memory_semantic_body_eat", "Parte para comer", "Memoria semántica cuerpo."),
        Triple("memory_semantic_body_walk", "Parte para caminar", "Memoria semántica cuerpo."),
        Triple("memory_semantic_body_write", "Parte para escribir", "Memoria semántica cuerpo."),
        Triple("memory_semantic_family_son", "Hijo del padre", "Memoria semántica familia."),
        Triple("memory_semantic_family_father_father", "Padre del padre", "Memoria semántica familia."),
        Triple("memory_semantic_family_sister", "Hija de la madre", "Memoria semántica familia."),
        Triple("memory_semantic_family_uncle", "Hermano de madre", "Memoria semántica familia."),
        Triple("memory_semantic_family_nephew", "Hijo del hermano", "Memoria semántica familia."),
        Triple("memory_semantic_object_umbrella", "Uso paraguas", "Memoria semántica pragmática."),
        Triple("memory_semantic_object_soap", "Uso jabón", "Memoria semántica pragmática."),
        Triple("memory_semantic_object_towel", "Uso toalla", "Memoria semántica pragmática."),
        Triple("memory_semantic_object_keys", "Uso llaves", "Memoria semántica pragmática."),
        Triple("memory_semantic_object_phone", "Uso teléfono", "Memoria semántica pragmática."),
        Triple("memory_semantic_object_tv", "Uso televisor", "Memoria semántica pragmática."),
        Triple("memory_semantic_object_radio", "Uso radio", "Memoria semántica pragmática."),
        Triple("memory_semantic_object_clock", "Uso reloj", "Memoria semántica pragmática."),
        Triple("memory_semantic_object_money", "Uso dinero", "Memoria semántica pragmática."),
        Triple("memory_semantic_object_wallet", "Uso cartera", "Memoria semántica pragmática."),
        Triple("memory_semantic_animal_hen", "Origen huevos", "Memoria semántica biológica."),
        Triple("memory_semantic_animal_cow", "Origen leche", "Memoria semántica biológica."),
        Triple("memory_semantic_animal_sheep", "Origen lana", "Memoria semántica biológica."),
        Triple("memory_semantic_animal_bee", "Origen miel", "Memoria semántica biológica."),
        Triple("memory_semantic_animal_spider", "Origen telaraña", "Memoria semántica biológica."),
        Triple("memory_semantic_food_bread", "Lugar compra pan", "Memoria semántica comercio."),
        Triple("memory_semantic_food_meat", "Lugar compra carne", "Memoria semántica comercio."),
        Triple("memory_semantic_food_fish", "Lugar compra pescado", "Memoria semántica comercio."),
        Triple("memory_semantic_food_fruit", "Lugar compra fruta", "Memoria semántica comercio."),
        Triple("memory_semantic_food_medicine", "Lugar compra medicina", "Memoria semántica comercio."),
        Triple("memory_semantic_place_church", "Lugar para misa", "Memoria semántica social."),
        Triple("memory_semantic_place_hospital", "Lugar para enfermos", "Memoria semántica social."),
        Triple("memory_semantic_place_school", "Lugar para aprender", "Memoria semántica social."),
        Triple("memory_semantic_place_park", "Lugar para pasear", "Memoria semántica social."),
        Triple("memory_semantic_place_kitchen", "Lugar para cocinar", "Memoria semántica hogar."),
        Triple("memory_semantic_place_bathroom", "Lugar para ducha", "Memoria semántica hogar."),
        Triple("memory_semantic_color_sun", "Color del sol", "Memoria semántica visual."),
        Triple("memory_semantic_color_tomato", "Color del tomate", "Memoria semántica visual."),
        Triple("memory_semantic_color_lemon", "Color del limón", "Memoria semántica visual."),
        Triple("memory_semantic_color_orange", "Color de naranja", "Memoria semántica visual."),
        Triple("memory_cultural_first_king", "Primer Rey democracia", "Memoria cultural política."),
        Triple("memory_cultural_civil_war", "Siglo Guerra Civil", "Memoria cultural histórica."),
        Triple("memory_cultural_discovery_navigator", "Navegante América", "Memoria cultural histórica."),
        Triple("memory_cultural_capital_france", "Capital de Francia", "Memoria cultural geográfica."),
        Triple("memory_cultural_capital_uk", "Capital Reino Unido", "Memoria cultural geográfica."),
        Triple("memory_cultural_euro_intro", "Año entrada Euro", "Memoria cultural histórica."),
        Triple("memory_cultural_olympics_barcelona", "Año Olimpiadas BCN", "Memoria cultural histórica."),
        Triple("memory_cultural_monalisa", "Pintor La Gioconda", "Memoria cultural artística."),
        Triple("memory_cultural_beethoven", "Compositor 9ª Sinfonía", "Memoria cultural musical."),
        Triple("memory_cultural_cervantes_birth", "Ciudad Cervantes", "Memoria cultural literaria.")
    ).sortedBy { it.second }
}

private fun getOrientationPool(): List<Triple<String, String, String>> {
    return listOf(
        Triple("orientation_temporal_day", "Día del mes", "Identificar el número del día actual."),
        Triple("orientation_temporal_month", "Mes del año", "Identificar en qué mes estamos."),
        Triple("orientation_temporal_year", "Año actual", "Reconocer el año en curso."),
        Triple("orientation_temporal_season", "Estación del año", "¿Es primavera, verano, otoño o invierno?"),
        Triple("orientation_temporal_dayweek", "Día de la semana", "¿Qué día de la semana es hoy?"),
        Triple("orientation_temporal_partday", "Parte del día", "Mañana, tarde o noche."),
        Triple("orientation_temporal_hour", "Hora aproximada", "Estimación del tiempo actual."),
        Triple("orientation_temporal_century", "Siglo actual", "Reconocimiento de la era actual."),
        Triple("orientation_temporal_decade", "Década actual", "Ubicación en la década."),
        Triple("orientation_temporal_yesterday", "Ayer qué día fue", "Orientación temporal retrospectiva."),
        Triple("orientation_temporal_tomorrow", "Mañana qué día será", "Orientación temporal prospectiva."),
        Triple("orientation_temporal_week_next", "Día próxima semana", "Cálculo de fechas futuras."),
        Triple("orientation_temporal_christmas", "Mes de Navidad", "Reconocimiento de festividades."),
        Triple("orientation_temporal_newyear", "Día Año Nuevo", "Inicio del ciclo anual."),
        Triple("orientation_temporal_night_light", "Luz nocturna", "Astronomía básica."),
        Triple("orientation_temporal_day_light", "Luz diurna", "Astronomía básica."),
        Triple("orientation_temporal_spring_month", "Mes de Primavera", "Ciclos estacionales."),
        Triple("orientation_temporal_summer_month", "Mes de Verano", "Ciclos estacionales."),
        Triple("orientation_temporal_autumn_month", "Mes de Otoño", "Ciclos estacionales."),
        Triple("orientation_temporal_winter_month", "Mes de Invierno", "Ciclos estacionales."),
        
        Triple("orientation_spatial_city", "Ciudad o Pueblo", "¿En qué localidad se encuentra?"),
        Triple("orientation_spatial_province", "Provincia", "¿En qué provincia estamos?"),
        Triple("orientation_spatial_country", "País", "Reconocimiento nacional."),
        Triple("orientation_spatial_continent", "Continente", "Ubicación geográfica global."),
        Triple("orientation_spatial_planet", "Planeta", "Ubicación espacial cósmica."),
        Triple("orientation_spatial_place", "Lugar actual", "Reconocimiento del entorno inmediato."),
        Triple("orientation_spatial_floor", "Planta edificio", "Orientación en altura."),
        Triple("orientation_spatial_kitchen", "Uso de cocina", "Funcionalidad de estancias."),
        Triple("orientation_spatial_bedroom", "Uso de dormitorio", "Funcionalidad de estancias."),
        Triple("orientation_spatial_library", "Uso de biblioteca", "Funcionalidad de estancias."),
        Triple("orientation_spatial_pharmacy", "Uso de farmacia", "Servicios de salud."),
        Triple("orientation_spatial_bakery", "Uso de panadería", "Servicios básicos."),
        Triple("orientation_spatial_ceiling", "Objeto en techo", "Percepción espacial."),
        Triple("orientation_spatial_ocean", "Océano mayor", "Geografía global."),
        Triple("orientation_spatial_moon_orbit", "Orbita lunar", "Conocimiento cósmico."),
        Triple("orientation_spatial_capital_spain", "Capital de España", "Geografía política."),
        
        Triple("orientation_personal_name", "Nombre propio", "Identidad personal básica."),
        Triple("orientation_personal_surname", "Primer apellido", "Identidad personal básica."),
        
        Triple("orientation_calc_year_days", "Días del año", "Conocimiento matemático temporal."),
        Triple("orientation_calc_year_months", "Meses del año", "Estructura del calendario."),
        Triple("orientation_calc_week_days", "Días de la semana", "Estructura semanal."),
        Triple("orientation_calc_day_hours", "Horas del día", "Estructura horaria."),
        Triple("orientation_calc_minutes_hour", "Minutos en una hora", "División del tiempo."),
        Triple("orientation_calc_seconds_minute", "Segundos en un minuto", "División del tiempo."),
        Triple("orientation_calc_half_day", "Horas medio día", "Cálculo fraccional."),
        Triple("orientation_calc_feet_count", "Número de pies", "Esquema corporal."),
        Triple("orientation_calc_hands_count", "Número de manos", "Esquema corporal."),
        Triple("orientation_calc_fingers_hand", "Dedos en una mano", "Conteo básico."),
        Triple("orientation_calc_fingers_total", "Dedos totales", "Suma básica."),
        Triple("orientation_calc_century_years", "Años en un siglo", "Escala histórica."),
        Triple("orientation_calc_decade_years", "Años en una década", "Escala histórica."),
        Triple("orientation_calc_dozen", "Unidades docena", "Unidades de medida."),
        Triple("orientation_calc_half_dozen", "Unidades media docena", "Unidades de medida."),
        Triple("orientation_calc_wheels_car", "Ruedas de coche", "Observación del entorno."),
        Triple("orientation_calc_wheels_bike", "Ruedas de bicicleta", "Observación del entorno."),
        Triple("orientation_calc_wheels_tricycle", "Ruedas de triciclo", "Observación del entorno."),

        Triple("orientation_situational_currency", "Moneda actual", "Contexto económico."),
        Triple("orientation_situational_language", "Idioma hablado", "Contexto cultural."),
        Triple("orientation_situational_color_sky", "Color del cielo", "Percepción ambiental."),
        Triple("orientation_situational_color_grass", "Color de hierba", "Percepción ambiental."),
        Triple("orientation_situational_king", "Rey actual", "Orientación sociopolítica."),
        Triple("orientation_situational_blood", "Color sangre", "Conocimiento biológico."),
        Triple("orientation_situational_fire", "Sensación fuego", "Termoalgesia cognitiva."),
        Triple("orientation_situational_ice", "Sensación hielo", "Termoalgesia cognitiva."),
        Triple("orientation_situational_sun", "Salida del sol", "Orientación natural."),
        Triple("orientation_situational_lemon_taste", "Sabor limón", "Gusto y memoria."),
        Triple("orientation_situational_sugar_taste", "Sabor azúcar", "Gusto y memoria."),
        Triple("orientation_situational_sea_water", "Sabor agua mar", "Conocimiento geográfico."),
        Triple("orientation_situational_stop_color", "Color señal STOP", "Seguridad vial."),
        Triple("orientation_situational_zebra_cross", "Color paso cebra", "Seguridad vial."),
        Triple("orientation_situational_traffic_light_go", "Semaforo verde", "Seguridad vial."),
        Triple("orientation_situational_traffic_light_stop", "Semaforo rojo", "Seguridad vial."),
        Triple("orientation_situational_dog_sound", "Sonido perro", "Reconocimiento onomatopéyico."),
        Triple("orientation_situational_cat_sound", "Sonido gato", "Reconocimiento onomatopéyico."),
        Triple("orientation_situational_cow_sound", "Sonido vaca", "Reconocimiento onomatopéyico."),
        Triple("orientation_situational_sheep_sound", "Sonido oveja", "Reconocimiento onomatopéyico."),
        Triple("orientation_situational_milk_color", "Color de leche", "Propiedades de objetos."),
        Triple("orientation_situational_coal_color", "Color de carbón", "Propiedades de objetos."),
        Triple("orientation_situational_tomato_color", "Color de tomate", "Propiedades de objetos."),
        Triple("orientation_situational_banana_color", "Color de plátano", "Propiedades de objetos."),
        Triple("orientation_situational_dentist", "Médico de dientes", "Red social de apoyo."),
        Triple("orientation_situational_umbrella", "Uso paraguas", "Pragmática de objetos."),
        Triple("orientation_situational_glasses", "Uso gafas", "Pragmática de objetos."),
        Triple("orientation_situational_shoes_wear", "Uso zapatos", "Pragmática de objetos."),
        Triple("orientation_situational_hat_wear", "Uso sombrero", "Pragmática de objetos."),
        Triple("orientation_situational_gloves_wear", "Uso guantes", "Pragmática de objetos."),
        Triple("orientation_situational_fridge_use", "Uso frigorífico", "Pragmática de objetos."),
        Triple("orientation_situational_chair_use", "Uso silla", "Pragmática de objetos."),
        Triple("orientation_situational_bed_use", "Uso cama", "Pragmática de objetos."),
        Triple("orientation_situational_eyes_count", "Número de ojos", "Esquema corporal."),
        Triple("orientation_situational_ears_count", "Número de orejas", "Esquema corporal."),
        Triple("orientation_situational_nose_count", "Número de narices", "Esquema corporal."),
        Triple("orientation_situational_mouth_count", "Número de bocas", "Esquema corporal."),
        Triple("orientation_situational_head_count", "Número de cabezas", "Esquema corporal."),
        Triple("orientation_situational_arms_count", "Número de brazos", "Esquema corporal."),
        Triple("orientation_situational_legs_count", "Número de piernas", "Esquema corporal."),
        Triple("orientation_situational_hair_color", "Color pelo anciano", "Identidad física."),
        Triple("orientation_situational_sun_shape", "Forma del sol", "Geometría natural."),
        Triple("orientation_situational_ball_shape", "Forma de balón", "Geometría de objetos."),
        Triple("orientation_situational_table_use", "Uso de mesa", "Pragmática funcional."),
        Triple("orientation_situational_knife_use", "Uso de cuchillo", "Pragmática funcional."),
        Triple("orientation_situational_spoon_use", "Uso de cuchara", "Pragmática funcional."),
        Triple("orientation_situational_comb_use", "Uso de peine", "Pragmática funcional."),
        Triple("orientation_situational_soap_use", "Uso de jabón", "Pragmática funcional."),
        Triple("orientation_situational_towel_use", "Uso de toalla", "Pragmática funcional."),
        Triple("orientation_situational_broom_use", "Uso de escoba", "Pragmática funcional."),
        Triple("orientation_situational_oven_use" , "Uso de horno", "Pragmática funcional."),
        Triple("orientation_situational_pill_use", "Uso de medicinas", "Conciencia de salud."),
        Triple("orientation_situational_phone_use", "Uso de teléfono", "Tecnología básica."),
        Triple("orientation_situational_keys_use", "Uso de llaves", "Seguridad del hogar."),
        Triple("orientation_situational_glasses_use", "Uso de gafas", "Compensación sensorial."),
        Triple("orientation_situational_watch_use", "Uso de reloj", "Orientación temporal externa."),
        Triple("orientation_situational_wallet_use", "Uso de cartera", "Gestión instrumental."),
        Triple("orientation_situational_calendar_use", "Uso de calendario", "Orientación temporal externa."),
        Triple("orientation_situational_doctor_tool", "Herramienta médico", "Entorno sanitario."),
        Triple("orientation_situational_firemen", "Función bomberos", "Seguridad ciudadana."),
        Triple("orientation_situational_stewardess", "Lugar azafata", "Transporte público."),
        Triple("orientation_situational_pilot", "Función piloto", "Transporte público."),
        Triple("orientation_situational_ship_captain", "Función capitán", "Transporte marítimo."),
        Triple("orientation_situational_cow_milk", "Origen leche", "Conocimiento animal."),
        Triple("orientation_situational_hen_eggs", "Origen huevos", "Conocimiento animal."),
        Triple("orientation_situational_bee_honey", "Origen miel", "Conocimiento animal."),
        Triple("orientation_situational_spider_web", "Origen tela araña", "Conocimiento animal.")
    ).sortedBy { it.second }
}

private data class Quadruple<A, B, C, D>(val first: A, val second: B, val third: C, val fourth: D)

@Composable
private fun SelectionCard(title: String, description: String, icon: ImageVector, activityCount: Int? = null, onClick: () -> Unit) {
    Card(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant,
            contentColor = MaterialTheme.colorScheme.onSurfaceVariant
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(modifier = Modifier.size(40.dp), shape = CircleShape, color = MaterialTheme.colorScheme.primaryContainer) {
                Box(contentAlignment = Alignment.Center) { Icon(icon, contentDescription = null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(20.dp)) }
            }
            Spacer(modifier = Modifier.width(16.dp))
            Column(modifier = Modifier.weight(1f)) {
                Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    Text(title, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleMedium)
                    Spacer(modifier = Modifier.weight(1f))
                    activityCount?.let {
                        Text("$it actividades", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary)
                    }
                }
                Text(description, style = MaterialTheme.typography.bodySmall, color = Color.Gray)
            }
        }
    }
}
